import axios from "axios";
import { API_BASE_URL, getActiveHospitalId } from "./constants";
import { handleApiError } from "../utils/errorHandler";

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 10000,
  headers: {
    'ngrok-skip-browser-warning': '69420',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

// CSRF & Hospital Context Interceptor
api.interceptors.request.use((config) => {
  // Read CSRF token from document cookie if available
  const match = document.cookie.match(new RegExp('(^| )XSRF-TOKEN=([^;]+)'));
  if (match) {
    config.headers['X-XSRF-TOKEN'] = decodeURIComponent(match[2]);
  }

  const currentHospitalId = getActiveHospitalId();

  // Inject active hospital_id across entire application
  config.params = {
    hospital_id: currentHospitalId,
    ...config.params
  };

  if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
    if (config.data.hospital_id === undefined) {
      config.data.hospital_id = currentHospitalId;
    }
  }

  return config;
}, (error) => Promise.reject(handleApiError(error)));

// Network Retry Interceptor with Centralized Error Handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (!config) return Promise.reject(handleApiError(error));

    // Max 3 retry attempts for network errors or 5xx server errors
    const MAX_RETRIES = 3;
    config._retryCount = config._retryCount || 0;

    const isNetworkError = !error.response || error.code === 'ECONNABORTED';
    const isServerError = error.response && error.response.status >= 500 && error.response.status < 600;

    if ((isNetworkError || isServerError) && config._retryCount < MAX_RETRIES) {
      config._retryCount += 1;
      const delayMs = Math.pow(2, config._retryCount) * 500; // 1s, 2s, 4s
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      return api(config);
    }

    // Attach normalized error object to error.normalized
    const normalized = handleApiError(error);
    error.normalized = normalized;

    // Handle 401 unauthorized session expiry globally while preserving subpath (/demo/v1, /demo/v2, /)
    if (normalized.status === 401 && window.location.pathname !== '/' && window.location.pathname !== '/demo/v1/' && window.location.pathname !== '/demo/v2/') {
      const subpath = window.location.pathname.startsWith('/demo/v1') ? '/demo/v1/' : (window.location.pathname.startsWith('/demo/v2') ? '/demo/v2/' : '/');
      window.location.href = `${subpath}?sessionExpired=true`;
    }

    return Promise.reject(normalized);
  }
);

export default api;